import { useCallback, useContext, useEffect, useState } from 'react'
import PageContent from '../../ui/PageContent'
import FormWrapper from '../../ui/form/FormWrapper'
import { AdminContext, OrganizationContext } from '../../contexts'
import TextInput from '../../ui/form/TextInput'
import SwitchField from '../../ui/form/SwitchField'
import { Organization, SAMLConfigParams, SAMLConfigResponse } from '../../types'
import Heading from '../../ui/Heading'
import api from '../../api'
import { env } from '../../config/env'
import { toast } from 'react-hot-toast/headless'
import { Alert, Button, InfoTable } from '../../ui'
import Modal from '../../ui/Modal'
import { useTranslation } from 'react-i18next'
import { checkOrganizationRole } from '../../utils'

export default function Settings() {
    const profile = useContext(AdminContext)
    const { t } = useTranslation()
    const [organization] = useContext(OrganizationContext)
    const [samlConfig, setSamlConfig] = useState<SAMLConfigResponse>()
    const [showSamlModal, setShowSamlModal] = useState(false)

    const owner = checkOrganizationRole('owner', profile?.role)

    const loadSamlConfig = useCallback(async () => {
        try {
            const config = await api.organizations.getSamlConfig()
            setSamlConfig(config)
        } catch {
            // Ignore errors loading SAML config
        }
    }, [])

    useEffect(() => {
        void loadSamlConfig()
    }, [loadSamlConfig])

    const deleteOrganization = async () => {
        if (confirm(t('delete_organization_confirm'))) {
            await api.organizations.delete()
            await api.auth.logout()
            window.location.href = '/'
        }
    }

    const handleDeleteSaml = async () => {
        if (confirm(t('saml_remove_confirm'))) {
            await api.organizations.deleteSamlConfig()
            await loadSamlConfig()
            toast.success(t('saml_removed'))
        }
    }

    const handleSaveSaml = async (data: SAMLConfigParams) => {
        await api.organizations.updateSamlConfig(data)
        await loadSamlConfig()
        setShowSamlModal(false)
        toast.success(t('saml_saved'))
    }

    const callbackUrl = `${env.api.baseURL}/auth/login/saml/callback`

    return (
        <>
            <PageContent title={t('settings')}>
                <FormWrapper<Organization>
                    defaultValues={organization}
                    onSubmit={async ({ username, domain, tracking_deeplink_mirror_url }) => {
                        await api.organizations.update(organization.id, { username, domain, tracking_deeplink_mirror_url })

                        toast.success(t('saved_organization_settings'))
                    }}
                    submitLabel={t('save_settings')}
                >
                    {form => <>
                        <Heading size="h3" title={t('general')} />
                        <TextInput.Field
                            form={form}
                            name="username"
                            subtitle={t('org_username_subtitle')}
                        />
                        <TextInput.Field
                            form={form}
                            name="domain"
                            subtitle={t('org_domain_subtitle')}
                        />
                        <Heading size="h3" title={t('tracking')} />
                        <TextInput.Field
                            form={form}
                            name="tracking_deeplink_mirror_url"
                            label={t('tracking_deeplink_mirror_url')}
                            subtitle={t('tracking_deeplink_mirror_url_subtitle')}
                        />
                    </>}
                </FormWrapper>

                <br />
                <Heading size="h3" title={t('saml_sso_heading')} />
                {samlConfig?.configured
                    ? (
                        <>
                            <Alert variant="info" title={t('saml_configured')}>
                                {t('saml_active_desc')}
                            </Alert>
                            <InfoTable rows={{
                                [t('saml_sso_url')]: samlConfig.entryPoint,
                                [t('saml_issuer')]: samlConfig.issuer,
                                [t('saml_acs_url')]: samlConfig.callbackUrl,
                                [t('saml_signed_response')]: samlConfig.wantAuthnResponseSigned ?? false,
                                [t('saml_auto_create_users')]: samlConfig.saml_auto_create ?? false,
                            }} />
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button onClick={() => setShowSamlModal(true)}>{t('saml_edit')}</Button>
                                <Button variant="destructive" onClick={handleDeleteSaml}>{t('saml_remove')}</Button>
                            </div>
                        </>
                    )
                    : (
                        <>
                            <p>{t('saml_not_configured_desc')}</p>
                            <Button onClick={() => setShowSamlModal(true)}>{t('saml_configure')}</Button>
                        </>
                    )}

                {owner && (
                    <>
                        <br />
                        <br />
                        <Heading size="h3" title={t('danger_zone')} />
                        <p>{t('delete_organization_desc')}</p>
                        <Button
                            variant="destructive"
                            onClick={async () => await deleteOrganization()}
                        >
                            {t('delete_organization')}
                        </Button>
                    </>
                )}
            </PageContent>

            <Modal
                open={showSamlModal}
                onClose={() => setShowSamlModal(false)}
                title={t('saml_configuration')}
                size="regular"
            >
                <FormWrapper<SAMLConfigParams>
                    defaultValues={samlConfig?.configured
                        ? {
                            name: samlConfig.name,
                            entryPoint: samlConfig.entryPoint ?? '',
                            issuer: samlConfig.issuer ?? '',
                            cert: samlConfig.cert ?? '',
                            wantAuthnResponseSigned: samlConfig.wantAuthnResponseSigned ?? false,
                            saml_auto_create: samlConfig.saml_auto_create ?? false,
                        }
                        : {
                            entryPoint: '',
                            issuer: '',
                            cert: '',
                            wantAuthnResponseSigned: false,
                            saml_auto_create: false,
                        }}
                    onSubmit={handleSaveSaml}
                    submitLabel={t('saml_save')}
                >
                    {form => <>
                        <TextInput.Field
                            form={form}
                            name="name"
                            label={t('saml_display_name')}
                            subtitle={t('saml_display_name_subtitle')}
                        />
                        <TextInput.Field
                            form={form}
                            name="entryPoint"
                            label={t('saml_entry_point')}
                            required
                            subtitle={t('saml_entry_point_subtitle')}
                        />
                        <TextInput.Field
                            form={form}
                            name="issuer"
                            label={t('saml_entity_id')}
                            required
                            subtitle={t('saml_entity_id_subtitle')}
                        />
                        <TextInput.Field
                            form={form}
                            name="cert"
                            label={t('saml_certificate')}
                            required
                            textarea
                            subtitle={t('saml_certificate_subtitle')}
                        />
                        <SwitchField
                            form={form}
                            name="wantAuthnResponseSigned"
                            label={t('saml_require_signed')}
                        />
                        <SwitchField
                            form={form}
                            name="saml_auto_create"
                            label={t('saml_auto_create_label')}
                            subtitle={t('saml_auto_create_subtitle')}
                        />
                        <Alert variant="info" title={t('saml_acs_callback')}>
                            {t('saml_acs_copy_instruction')}<br />
                            <code style={{ wordBreak: 'break-all' }}>{callbackUrl}</code>
                        </Alert>
                    </>}
                </FormWrapper>
            </Modal>
        </>
    )
}
